import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MdbModalRef } from 'mdb-angular-ui-kit/modal';
import { allCountries } from 'country-telephone-data';

@Component({
  selector: 'app-edit-profile',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './edit-profile.component.html',
  styleUrls: ['./edit-profile.component.css']
})
export class EditProfileComponent implements OnInit, OnChanges {
  @Input() profileData: any = {};
  @Input() profileDesignation: any = {}; 
  @Input() IsRecruiter: boolean | undefined;
  @Input() onSaveCallback!: (updated: any) => void;

  MobileCountries = allCountries.map(c => ({
    name: c.name,
    dialCode: c.dialCode,
    iso2: c.iso2
  }));

  profileForm!: FormGroup;
  countries: any;
  educationLevels: any;
  experienceYears: any;
  aboutMeLength: number = 0;
  designation: any;

  constructor(
    public modalRef: MdbModalRef<EditProfileComponent>,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.setupForm();
    this.applyRoleValidators(this.IsRecruiter === true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['IsRecruiter'] && this.profileForm) {
      this.applyRoleValidators(this.IsRecruiter === true);
    }
    // If profileData comes in after init:
    if (changes['profileData'] && !changes['profileData'].firstChange) {
      this.setupForm();
      this.applyRoleValidators(this.IsRecruiter === true);
    }
  }

  setupForm(): void {
    const data = this.profileData || {};
    this.countries = data.countries;
    this.educationLevels = data.educationLevels;
    this.experienceYears = data.experienceYears;
    this.aboutMeLength = (data.about_me || '').length;

    this.designation = this.profileDesignation?.designation || this.profileDesignation?.name || '';

    this.profileForm = this.fb.group({
      name: [data.name || '', Validators.required],
      country_id: [data.country_id || ''],                 // required set dynamically
      experience_years_id: [data.experience_years_id || ''], // required set dynamically
      education_level_id: [data.education_level_id || ''], // optional
      country_code: [data.country_code?.replace('+', '') || ''],
      phone: [''],
      about_me: [data.about_me || '', [Validators.maxLength(2500)]],
      designation: [this.designation || '', Validators.required]
    });

    // Normalize phone shown in the input
    if (data.phone && data.country_code) {
      const dialCode = data.country_code.replace('+', '');
      let nationalNumber = (data.phone as string).replace(/\D/g, '');
      const stripZeroDialCodes = ['60', '880', '91', '92', '234'];
      if (nationalNumber.startsWith('0') && stripZeroDialCodes.includes(dialCode)) {
        nationalNumber = nationalNumber.substring(1);
      }
      this.profileForm.patchValue({ phone: nationalNumber });
    }
  }

  /** Dynamically apply validators & enabled/disabled state based on role */
  private applyRoleValidators(isRecruiter: boolean): void {
    const f = this.profileForm;
    if (!f) return;

    const name = f.get('name');
    const designation = f.get('designation');
    const country_id = f.get('country_id');
    const experience_years_id = f.get('experience_years_id');
    const education_level_id = f.get('education_level_id');
    const country_code = f.get('country_code');
    const phone = f.get('phone');

    // Always required
    name?.setValidators([Validators.required]);
    designation?.setValidators([Validators.required]);

    if (isRecruiter) {
      // Recruiter: keep only name & designation required
      country_id?.clearValidators();
      experience_years_id?.clearValidators();
      // Optional: ensure education remains optional
      education_level_id?.clearValidators();

      // Disable fields that are hidden for recruiters so they don't affect validity or payload
      country_id?.disable({ emitEvent: false });
      experience_years_id?.disable({ emitEvent: false });
      education_level_id?.disable({ emitEvent: false });

      // Phone is optional by default; leave enabled so they can edit if they want
      country_code?.enable({ emitEvent: false });
      phone?.enable({ emitEvent: false });
    } else {
      // Non-recruiter: enforce the existing rules
      country_id?.setValidators([Validators.required]);
      experience_years_id?.setValidators([Validators.required]);
      // Education stays optional
      education_level_id?.clearValidators();

      // Enable them back
      country_id?.enable({ emitEvent: false });
      experience_years_id?.enable({ emitEvent: false });
      education_level_id?.enable({ emitEvent: false });
      country_code?.enable({ emitEvent: false });
      phone?.enable({ emitEvent: false });
    }

    // Update validity after changing validators
    name?.updateValueAndValidity({ emitEvent: false });
    designation?.updateValueAndValidity({ emitEvent: false });
    country_id?.updateValueAndValidity({ emitEvent: false });
    experience_years_id?.updateValueAndValidity({ emitEvent: false });
    education_level_id?.updateValueAndValidity({ emitEvent: false });
    country_code?.updateValueAndValidity({ emitEvent: false });
    phone?.updateValueAndValidity({ emitEvent: false });
  }

  private removeEmpty(obj: any): any {
    return Object.fromEntries(
      Object.entries(obj).filter(([_, v]) => {
        if (v === null || v === undefined) return false;
        if (typeof v === 'string' && v.trim() === '') return false;
        return true;
      })
    );
  }

  private mapToDesignationDto(raw: any): { id: number | null; designation: string } | null {
    if (raw == null) return null;
    const rawId = raw.id ?? raw.designation_id ?? raw.value ?? null;
    const numericId = (rawId === null || rawId === undefined || rawId === '') ? null : Number(rawId);
    const hasValidId = typeof numericId === 'number' && !Number.isNaN(numericId);
    const name =
      (typeof raw === 'string'
        ? raw
        : (raw.designation ?? raw.name ?? raw.label ?? '')
      ).toString().trim();
    if (!name) return null;
    return { id: hasValidId ? numericId : null, designation: name };
  }

  saveChanges(): void {
    if (!this.profileForm) return;

    // When controls are disabled (recruiter mode), they won’t block validity nor appear in value()
    if (this.profileForm.invalid) {
      this.profileForm.markAllAsTouched();
      return;
    }

    // Normalize phone
    let phone: string = (this.profileForm.get('phone')?.value || '').trim();
    const dialCode: string = this.profileForm.get('country_code')?.value || '';
    const stripZeroDialCodes = ['60', '880', '91', '92', '234'];
    phone = phone.replace(/\D/g, '');
    if (phone.startsWith('0') && stripZeroDialCodes.includes(dialCode)) {
      phone = phone.substring(1);
    }

    // IMPORTANT: when some controls are disabled we need getRawValue() if you still want them included.
    // Here we intentionally use .value to exclude disabled fields from recruiter payload.
    const { designation, ...restForm } = this.profileForm.value || {};

    const profilePayload = this.removeEmpty({
      ...restForm,
      phone,
      country_code: dialCode ? `+${dialCode}` : ''
    });

    const fromForm = this.mapToDesignationDto(designation);             
    const fromExisting = this.mapToDesignationDto(this.profileDesignation); 

    let merged: { id: number | null; designation: string } | null = null;
    if (fromForm || fromExisting) {
      merged = {
        id: (fromForm?.id ?? fromExisting?.id ?? null),
        designation: (fromForm?.designation ?? fromExisting?.designation ?? '').trim()
      };
      if (!merged.designation) merged = null;
    }

    const otherPayload = {
      designations: merged ? [merged] : []
    };

    this.onSaveCallback?.({ profilePayload, otherPayload });
    this.modalRef.close();
  }

  onAboutMeChange(): void {
    this.aboutMeLength = this.profileForm?.get('about_me')?.value?.length || 0;
  }
}
